import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Container, Typography } from "@material-ui/core";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  ALERT_TEXT,
  PAGES_TITLES,
  TITLE_BUTTONS,
  TITLES,
} from "../../constants/titles";
import {
  VENUE_FIELDS_TITLE,
  dtoFormTeamFields,
  formTeamFields,
} from "../../constants/data";

import {
  deleteTeam,
  createTeam,
  updateTeam,
  getGoverningBodies,
} from "./sliceReducer";

import {
  CREATE_TEAM,
  CREATE_VENUE,
  DELETE_TEAM,
  EDIT_TEAM,
  GET_ALL_GOVERNING_BODIES,
  GET_CONFERENCES,
  GET_STATES_BY_COUNTRY_ISO,
} from "../../constants/endpoints";

import { getList } from "../../dataProvider/API";
import { getFillDtoTeam, getFillDtoVenues } from "../../utils/getFillDto";
import { MAPS_TYPES, toVenueOption } from "../../utils/mapsAutofill";
import { teamAdminApi } from "../../utils/teamAdminApi";
import { keepState } from "../../utils/returnTo";
import { createVenue } from "../Venues/sliceReducer";

import { getAllConferenceID } from "../../Redux/Reducers/commonReducer";

import { useRuleStyles } from "../Rules/styles";
import { useDebouncedCommit } from "../Rules/common";
import {
  clearedFilters,
  hasFilters,
  readQuery,
  toApiParams,
  writeQuery,
} from "./teamListQuery";
import TeamFilters from "./TeamFilters";
import TeamsTable from "./TeamsTable";
import TeamCreateForm from "../../containers/TeamCreateForm";
import ConfirmDialog from "../../components/ConfirmDialog";
import ButtonsGroup from "../../components/ButtonsGroup";
import CreateFromLinkDialog from "../../containers/CreateFromLinkDialog";
import FieldOfCreate from "../../components/FieldOfCreate";

// Venue step of the "team and venue" flow reuses the venue page's generic form.
const venueFieldsForForm = Object.entries(VENUE_FIELDS_TITLE).filter(
  ([key]) => key !== "id",
);

/**
 * Contact fields are rendered in the form as FLAT fields (city, state, postalCode, etc.)
 * but the API DTO uses contactInformation nested.
 *
 * Convert flat form params -> nested DTO shape.
 */
const nestContactInformation = (flatParams, selectedDto) => {
  const ciKeys = (formTeamFields?.contactInformation?.data || []).map(
    ({ short }) => short,
  );

  const contactInformation = {};
  const rest = { ...flatParams };

  ciKeys.forEach((k) => {
    if (k in rest) {
      contactInformation[k] = rest[k];
      delete rest[k];
    }
  });

  if (selectedDto?.contactInformationID && contactInformation.id == null) {
    contactInformation.id = selectedDto.contactInformationID;
  }

  return { ...rest, contactInformation };
};

const EMPTY_PAGE = { teamsDto: [], totalElements: 0, currentPage: 0 };

const Teams = () => {
  const classes = useRuleStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { governingBodies } = useSelector((state) => state.teams);
  const { conferenceID } = useSelector((state) => state.common);

  // ---- list state: filters, sort and page live in the URL --------------------------------
  const [listState, setListState] = useState(() => readQuery(location.search));
  const [searchDraft, setSearchDraft] = useState(listState.query);
  const [cityDraft, setCityDraft] = useState(listState.city);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [listLoading, setListLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    divisions: [],
    teamTypes: [],
    countries: [],
  });
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const requestSeq = useRef(0);

  const dtos = result.teamsDto || [];

  const [open, setOpen] = useState(false);
  const [defaultValueForm, setDefaultValueForm] = useState(null);
  const [selectedDto, setSelectedDto] = useState({});
  const [isViewCoachEmail, setIsViewCoachEmail] = useState(true);
  const [governingBodiesTitle, setGoverningBodiesTitle] = useState([]);
  const [conference, setConference] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectID, setSelectID] = useState(null);
  const [openFromLink, setOpenFromLink] = useState(false);
  const [venueStepValue, setVenueStepValue] = useState(null);
  const [pendingTeamValues, setPendingTeamValues] = useState(null);
  const [logoPreview, setLogoPreview] = useState(undefined);

  const isEditMode = !isEmpty(selectedDto);

  // Only the latest request may update the table, so a slow page can't overwrite a newer one.
  const reloadTeams = useCallback(() => {
    const seq = ++requestSeq.current;
    setListLoading(true);
    return teamAdminApi
      .list(toApiParams(listState))
      .then((data) => {
        if (seq === requestSeq.current) setResult(data || EMPTY_PAGE);
      })
      .catch((e) => {
        if (seq === requestSeq.current) toast.error(e.message);
      })
      .finally(() => {
        if (seq === requestSeq.current) setListLoading(false);
      });
  }, [listState]);

  useDebouncedCommit(searchDraft, (query) =>
    setListState((prev) =>
      prev.query === query ? prev : { ...prev, query, page: 0 },
    ),
  );
  useDebouncedCommit(cityDraft, (city) =>
    setListState((prev) =>
      prev.city === city ? prev : { ...prev, city, page: 0 },
    ),
  );

  const patchList = (changes) =>
    setListState((prev) => ({ ...prev, page: 0, ...changes }));

  const onSort = (key) =>
    setListState((prev) => ({
      ...prev,
      page: 0,
      sort: key,
      isAscending: prev.sort === key ? !prev.isAscending : true,
    }));

  const onPage = ({ page, limit }) =>
    setListState((prev) => {
      const size = Number(limit) || prev.size;
      return { ...prev, size, page: size === prev.size ? page : 0 };
    });

  const clearFilters = () => {
    setSearchDraft("");
    setCityDraft("");
    setListState((prev) => clearedFilters(prev));
  };

  const resolveConferenceDto = (conferenceList, conferenceId) => {
    if (conferenceId == null || conferenceId === "") return null;
    return conferenceList.find(({ id }) => id === conferenceId) || null;
  };

  const handleClickOpen = (data) => {
    setOpen(true);

    if (data?.id) {
      // Always hydrate from the canonical list.
      const fullDto = dtos.find((t) => t.id === data.id) || data;

      setSelectedDto(fullDto);
      setDefaultValueForm(fullDto);
      setIsViewCoachEmail(false);
    } else {
      setSelectedDto({});
      setDefaultValueForm({});
      setIsViewCoachEmail(true);
    }
  };

  const handleClose = () => {
    setDefaultValueForm(null);
    setOpen(false);
    setSelectedDto({});
    setLogoPreview(undefined);
  };

  const handleOpenFromLink = () => setOpenFromLink(true);
  const handleCloseFromLink = () => setOpenFromLink(false);

  const handleCloseVenueStep = () => {
    setVenueStepValue(null);
    setPendingTeamValues(null);
  };

  /**
   * Receives the drafts from CreateFromLinkDialog.
   *
   * Team only        -> open the team form pre-filled.
   * Team and venue   -> open the VENUE form first. The venue is created for real on
   *                     submit, and only then does the team form open with that venue
   *                     already linked. Reviewing the venue first matters because
   *                     timezone is required and Google can't always supply one.
   */
  const handleApplyFromLink = ({
    includeVenue,
    teamValues,
    venueValues,
    logo,
  }) => {
    setOpenFromLink(false);

    const teamDraft = {
      ...(teamValues || {}),
      ...(logo?.cloudAlias
        ? { cloudAlias: logo.cloudAlias, logoChanged: true }
        : {}),
    };

    if (includeVenue && venueValues) {
      setPendingTeamValues({ teamDraft, previewUrl: logo?.previewUrl });
      setVenueStepValue(venueValues);
      return;
    }

    setSelectedDto({});
    setIsViewCoachEmail(true);
    setLogoPreview(logo?.previewUrl);
    setDefaultValueForm(teamDraft);
    setOpen(true);
  };

  /**
   * Venue step of the "team and venue" flow: create the venue, then carry its id into
   * the team form. The team form's `venues` field holds whole venue objects, so the
   * created DTO is passed through rather than just its id.
   */
  const onSubmitVenueStep = (params) => {
    return dispatch(
      createVenue({
        endpoint: CREATE_VENUE,
        data: getFillDtoVenues(params),
      }),
    )
      .unwrap()
      .then((createdVenue) => {
        const venueOption = toVenueOption(createdVenue);
        const pending = pendingTeamValues;

        // The venue exists either way; only the automatic link failed, so say so
        // rather than letting the team silently come out with no venue attached.
        if (!venueOption) {
          toast.warn(ALERT_TEXT.MAPS_VENUE_LINK_FAILED);
        }

        handleCloseVenueStep();

        // The venue is saved but the team is not — make that explicit so the form
        // that just appeared isn't mistaken for a confirmation screen.
        toast.info(ALERT_TEXT.MAPS_VENUE_CREATED_TEAM_PENDING);

        setSelectedDto({});
        setIsViewCoachEmail(true);
        setLogoPreview(pending?.previewUrl);
        setDefaultValueForm({
          ...(pending?.teamDraft || {}),
          venues: venueOption ? [venueOption] : [],
        });
        setOpen(true);
      });
  };

  const handleOpenModal = (team) => {
    setSelectID(team?.id ?? null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectID(null);
    setOpenModal(false);
  };

  // IMPORTANT: return promise + unwrap so ButtonsGroup can await and only close on success
  const deleteOneTeam = () => {
    if (!selectID) {
      return Promise.reject(new Error("No team selected for deletion."));
    }

    return dispatch(deleteTeam(DELETE_TEAM(selectID)))
      .unwrap()
      .then(() => reloadTeams());
  };

  const updateOneTeam = (params) => {
    const oldTeamData = dtos.find(({ id }) => id === selectedDto.id) || {};

    const data = getFillDtoTeam(
      {
        ...params,
        id: selectedDto?.id,
        conference: resolveConferenceDto(conference, params?.conference),
      },
      oldTeamData,
      selectedDto?.contactInformationID,
    );

    return dispatch(updateTeam({ endpoint: EDIT_TEAM, data }))
      .unwrap()
      .then(() => reloadTeams());
  };

  const createOneTeam = (params) => {
    const data = getFillDtoTeam(
      {
        ...params,
        conference: resolveConferenceDto(conference, params?.conference),
      },
      dtoFormTeamFields,
    );

    return dispatch(createTeam({ endpoint: CREATE_TEAM, data }))
      .unwrap()
      .then(() => reloadTeams());
  };

  // IMPORTANT: do NOT close modal until create/update succeeds
  const onSubmit = (rawParams) => {
    // ✅ Re-nest contact info BEFORE fillDtoTeam
    const params = nestContactInformation(rawParams, selectedDto);

    if (isEditMode) {
      const { cloudAlias, logoUrl } = selectedDto;

      return updateOneTeam({
        ...params,
        cloudAlias:
          params.cloudAlias === cloudAlias ? logoUrl : params.cloudAlias,
      }).then(() => {
        handleClose();
      });
    }

    return createOneTeam(params).then(() => {
      handleClose();
    });
  };

  useEffect(() => {
    dispatch(
      getGoverningBodies({
        endpoint: GET_ALL_GOVERNING_BODIES,
      }),
    );

    dispatch(
      getAllConferenceID({
        endpoint: GET_CONFERENCES,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!isEmpty(governingBodies)) {
      const sortedGoverningBodies = [...governingBodies].sort((a, b) =>
        a.abbreviation.localeCompare(b.abbreviation),
      );
      setGoverningBodiesTitle(sortedGoverningBodies);
    }
  }, [governingBodies]);

  useEffect(() => {
    if (!isEmpty(conferenceID)) {
      setConference(conferenceID);
    }
  }, [conferenceID]);

  useEffect(() => {
    teamAdminApi
      .filterOptions()
      .then((options) =>
        setFilterOptions({
          divisions: options?.divisions || [],
          teamTypes: options?.teamTypes || [],
          countries: options?.countries || [],
        }),
      )
      .catch((e) => toast.error(e.message));
  }, []);

  // States of the chosen country, for the State filter.
  useEffect(() => {
    if (!listState.country) {
      setStates([]);
      return undefined;
    }
    let current = true;
    setStatesLoading(true);
    getList(GET_STATES_BY_COUNTRY_ISO(listState.country))
      .then((list) => current && setStates(Array.isArray(list) ? list : []))
      .catch(() => current && setStates([]))
      .finally(() => current && setStatesLoading(false));
    return () => {
      current = false;
    };
  }, [listState.country]);

  // Load, then mirror the list state into the URL.
  useEffect(() => {
    reloadTeams();
    const nextSearch = writeQuery(listState);
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true, state: keepState(location) },
      );
    }
    // location is intentionally not a dependency: this effect writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState]);

  // The URL changed from outside (nav link, back button, a pasted link): follow it.
  useEffect(() => {
    const fromUrl = readQuery(location.search);
    if (writeQuery(fromUrl) === writeQuery(listState)) return;
    setListState(fromUrl);
    setSearchDraft(fromUrl.query);
    setCityDraft(fromUrl.city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const total = result.totalElements || 0;

  return (
    <Box className={classes.page}>
      <Container maxWidth={false}>
        <Box className={classes.header}>
          <Box>
            <Typography component="h1" className={classes.title}>
              {PAGES_TITLES.TEAMS}
            </Typography>
            <Typography className={classes.subtitle} aria-live="polite">
              {listLoading && !total
                ? "Loading teams…"
                : `${total} team${total === 1 ? "" : "s"}`}
            </Typography>
          </Box>
          <Box display="flex" style={{ gap: 12 }} flexWrap="wrap">
            <Button
              variant="outlined"
              className={classes.touch}
              onClick={handleOpenFromLink}
            >
              {TITLE_BUTTONS.CREATE_FROM_LINK}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.touch}
              onClick={() => handleClickOpen()}
            >
              {TITLE_BUTTONS.CREATE_TEAM}
            </Button>
          </Box>
        </Box>

        <TeamFilters
          state={listState}
          patch={patchList}
          searchDraft={searchDraft}
          onSearchDraft={setSearchDraft}
          cityDraft={cityDraft}
          onCityDraft={setCityDraft}
          governingBodies={
            Array.isArray(governingBodies) ? governingBodies : []
          }
          countries={filterOptions.countries}
          states={states}
          statesLoading={statesLoading}
          divisions={filterOptions.divisions}
          teamTypes={filterOptions.teamTypes}
          canClear={hasFilters(listState)}
          onClear={clearFilters}
        />

        <TeamsTable
          rows={dtos}
          state={listState}
          total={total}
          loading={listLoading}
          onSort={onSort}
          onPage={onPage}
          onEdit={handleClickOpen}
          onDelete={handleOpenModal}
        />
      </Container>

      {open && (
        <ConfirmDialog
          open
          handleClose={handleClose}
          title={isEditMode ? TITLES.EDIT_TEAM : TITLES.CREATE_TEAM}
          disableBackdropClose
        >
          {!!defaultValueForm ? (
            <TeamCreateForm
              defaultValue={defaultValueForm}
              handleClose={handleClose}
              onSubmit={onSubmit}
              isViewField={isViewCoachEmail}
              governingBodiesTitle={governingBodiesTitle}
              conference={conference}
              logoPreview={logoPreview}
            />
          ) : null}
        </ConfirmDialog>
      )}

      {openFromLink && (
        <ConfirmDialog
          open
          handleClose={handleCloseFromLink}
          title={TITLES.CREATE_TEAM_FROM_LINK}
          disableBackdropClose
        >
          <CreateFromLinkDialog
            open
            type={MAPS_TYPES.TEAM}
            onCancel={handleCloseFromLink}
            onApply={handleApplyFromLink}
          />
        </ConfirmDialog>
      )}

      {!!venueStepValue && (
        <ConfirmDialog
          open
          handleClose={handleCloseVenueStep}
          title={TITLES.CREATE_VENUE}
          disableBackdropClose
        >
          <FieldOfCreate
            dataForForms={venueFieldsForForm}
            defaultValue={venueStepValue}
            handleClose={handleCloseVenueStep}
            onSubmit={onSubmitVenueStep}
          />
        </ConfirmDialog>
      )}

      <ConfirmDialog
        open={openModal}
        handleClose={handleCloseModal}
        title={TITLES.DELETE_MODAL}
      >
        <ButtonsGroup
          handleClose={handleCloseModal}
          handleFunction={deleteOneTeam}
          submitTitle={TITLE_BUTTONS.DELETE}
          closeTitle={TITLE_BUTTONS.CANCEL}
        />
      </ConfirmDialog>
    </Box>
  );
};

export default Teams;
